import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import api, { apiError } from "@/lib/api";
import { RECORRIDO } from "@/constants/testIds";
import { PhotoCapture } from "@/components/PhotoCapture";
import { RECORRIDO_AREAS, HORARIOS_RONDAS } from "@/lib/recorridoConfig";
import { syncManager } from "@/lib/syncManager";
import { toast } from "sonner";
import { feedback } from "@/lib/feedback";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, Save, Shield, CheckCircle2, XCircle } from "lucide-react";

export default function RecorridoForm() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [form, setForm] = useState({
    titulo: `Recorrido de Seguridad - ${new Date().toLocaleDateString()}`,
    ubicacion: "Planta NAF",
    turno: "",
    fecha: new Date().toISOString(),
    checklist: {}, // { item_id: { estado: 'bueno' | 'malo', obs: '' } }
    fotos: [],
    novedad: "",
    estado: "sin_novedad",
    severidad: "baja",
  });

  const createMutation = useMutation({
    mutationFn: (body) => api.post("/recorridos", body).then((res) => res.data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["recorridos"] });
      qc.invalidateQueries({ queryKey: ["dashboard-stats"] });
      toast.success("Recorrido guardado correctamente");
      feedback.success();
      navigate("/recorridos");
    },
    onError: (e) => { toast.error(apiError(e, "Error al guardar")); feedback.error(); },
  });

  const handleCheck = (itemId, status) => {
    setForm(prev => ({
      ...prev,
      checklist: {
        ...prev.checklist,
        [itemId]: { ...prev.checklist[itemId], estado: status }
      },
      // Si hay algo mal, marcar como "con novedad" automáticamente
      estado: status === 'malo' ? 'con_novedad' : prev.estado
    }));
  };

  const handleObs = (itemId, val) => {
    setForm(prev => ({
      ...prev,
      checklist: {
        ...prev.checklist,
        [itemId]: { ...prev.checklist[itemId], obs: val }
      }
    }));
  };

  const submit = (e) => {
    e.preventDefault();
    if (!form.turno) return toast.error("El turno es obligatorio");

    if (syncManager.isOffline()) {
      syncManager.addToQueue("/recorridos", "POST", form, `Recorrido ${form.turno}`);
      navigate("/recorridos");
      return;
    }

    createMutation.mutate(form);
  };

  return (
    <div className="p-4 lg:p-8 max-w-5xl mx-auto">
      <header className="flex items-center justify-between mb-8">
        <div className="flex items-center gap-4">
          <button onClick={() => navigate("/recorridos")} className="p-2 hover:bg-secondary rounded-full transition-colors">
            <ArrowLeft size={20} />
          </button>
          <div>
            <h1 className="font-heading text-3xl font-extrabold text-navy">Nueva Ronda de Seguridad</h1>
            <p className="text-sm text-muted-foreground">Formato oficial NAF - Control de Instalaciones</p>
          </div>
        </div>
      </header>

      <form onSubmit={submit} className="space-y-8">
        <section className="bg-white border border-border p-6 shadow-sm">
          <h2 className="font-heading text-lg font-bold text-navy mb-4 flex items-center gap-2">
            <Shield size={18} /> Datos Generales
          </h2>
          <div className="grid md:grid-cols-3 gap-6">
            <div className="space-y-1.5">
              <label className="text-xs font-bold uppercase tracking-wider text-muted-foreground">Turno</label>
              <select
                value={form.turno}
                onChange={e => setForm({...form, turno: e.target.value})}
                className="input h-11"
                required
              >
                <option value="">Seleccionar turno...</option>
                <option value="1ro">1er Turno (Matutino)</option>
                <option value="2do">2do Turno (Vespertino)</option>
                <option value="3ro">3er Turno (Nocturno)</option>
              </select>
            </div>
            <div className="space-y-1.5">
              <label className="text-xs font-bold uppercase tracking-wider text-muted-foreground">Ubicación</label>
              <input
                value={form.ubicacion}
                onChange={e => setForm({...form, ubicacion: e.target.value})}
                className="input h-11"
              />
            </div>
            <div className="space-y-1.5">
              <label className="text-xs font-bold uppercase tracking-wider text-muted-foreground">Severidad de la Ronda</label>
              <select
                value={form.severidad}
                onChange={e => setForm({...form, severidad: e.target.value})}
                className="input h-11"
              >
                <option value="baja">Baja (Ronda normal)</option>
                <option value="media">Media (Observaciones)</option>
                <option value="alta">Alta (Incidente crítico)</option>
              </select>
            </div>
          </div>
        </section>

        <section className="bg-white border border-border overflow-hidden shadow-sm rounded-lg">
          <div className="bg-navy text-white p-4">
            <h2 className="font-heading font-bold text-center">CHECKLIST DE INSPECCIÓN</h2>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm border-collapse">
              <thead>
                <tr className="bg-secondary border-b border-border text-[10px] uppercase font-bold text-muted-foreground">
                  <th className="px-3 py-3 text-left">Área</th>
                  <th className="px-2 py-3 text-center w-16">Bien</th>
                  <th className="px-2 py-3 text-center w-16">Mal</th>
                  <th className="px-3 py-3 text-left min-w-[120px]">Obs.</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {RECORRIDO_AREAS.map((group) => (
                  <>
                    <tr key={group.area} className="bg-secondary/30">
                      <td colSpan={4} className="px-3 py-2 font-bold text-navy bg-navy/5 text-xs uppercase tracking-wider">{group.area}</td>
                    </tr>
                    {group.items.map((item) => (
                      <tr key={item.id} className="hover:bg-secondary/10 transition-colors">
                        <td className="px-3 py-4 font-medium text-navy text-xs leading-tight">{item.label}</td>
                        <td className="px-1 py-3 text-center">
                          <button
                            type="button"
                            onClick={() => handleCheck(item.id, 'bueno')}
                            className={`p-3 rounded-full transition-all active:scale-90 ${form.checklist[item.id]?.estado === 'bueno' ? 'bg-success text-white' : 'bg-secondary/50 text-muted-foreground'}`}
                          >
                            <CheckCircle2 size={24} />
                          </button>
                        </td>
                        <td className="px-1 py-3 text-center">
                          <button
                            type="button"
                            onClick={() => handleCheck(item.id, 'malo')}
                            className={`p-3 rounded-full transition-all active:scale-90 ${form.checklist[item.id]?.estado === 'malo' ? 'bg-critical text-white' : 'bg-secondary/50 text-muted-foreground'}`}
                          >
                            <XCircle size={24} />
                          </button>
                        </td>
                        <td className="px-3 py-3">
                          <input
                            placeholder="..."
                            value={form.checklist[item.id]?.obs || ""}
                            onChange={e => handleObs(item.id, e.target.value)}
                            className="w-full bg-secondary/20 border-none rounded px-2 py-2 text-xs focus:ring-1 focus:ring-navy outline-none"
                          />
                        </td>
                      </tr>
                    ))}
                  </>
                ))}
              </tbody>
            </table>
          </div>
        </section>

        <section className="grid md:grid-cols-2 gap-8">
          <div className="space-y-6">
            <PhotoCapture
              label="Evidencia Fotográfica"
              value={form.fotos}
              onChange={v => setForm({...form, fotos: v})}
            />
            <div className="space-y-1.5">
              <label className="text-xs font-bold uppercase tracking-wider text-muted-foreground">Resumen de Novedades (Interior/Exterior)</label>
              <textarea
                value={form.novedad}
                onChange={e => setForm({...form, novedad: e.target.value})}
                placeholder="Describe cualquier hallazgo importante aquí..."
                className="input min-h-[120px] py-3"
              />
            </div>
          </div>

          <div className="bg-navy/5 border border-dashed border-navy/20 p-8 flex flex-col items-center justify-center text-center rounded-lg">
             <Shield className="text-navy/20 mb-4" size={64} />
             <h3 className="font-heading text-xl font-bold text-navy">Finalizar Ronda</h3>
             <p className="text-sm text-muted-foreground mb-6 max-w-xs">
               Al guardar, se generará el reporte en Google Sheets y se subirán las fotos a la carpeta de evidencias.
             </p>
             <button
                type="submit"
                disabled={createMutation.isPending}
                className="w-full h-14 bg-navy text-white font-bold rounded-md hover:bg-navy-hover transition-all flex items-center justify-center gap-3 shadow-lg hover:shadow-navy/20"
             >
               {createMutation.isPending ? "Guardando..." : <><Save size={20} /> GUARDAR RECORRIDO</>}
             </button>
          </div>
        </section>
      </form>
    </div>
  );
}
